import {
  AppBar,
  Button,
  Drawer,
  Hidden,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import MenuIcon from '@material-ui/icons/Menu';
import { useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import DrawerContent from './DrawerContent';
import FloatingAvatar from './FloatingAvatar';
import { Loading } from './Loading';
import RealtimeHandler from './RealtimeHandler';
import { useSessionContext } from '../../contexts/session-context';
import { AccountQueryQuery } from 'generated/graphql';
import { useEhrContext } from '../../contexts/ehr-context';
import NewBookButton from '../NewBookButton';

const drawerWidth = 270;
const userMenuHeight = 56;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  title: {
    flexGrow: 1,
  },
  drawer: {
    [theme.breakpoints.up('md')]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  drawerPaper: {
    width: drawerWidth,
  },
  progressContainer: {
    width: '100%',
  },
  content: {
    flexGrow: 1,
  },
  contentInternal: {
    display: 'flex',
    height: `calc(100vh - ${userMenuHeight}px)`,
    overflow: 'auto',
  },
  contentInternalWithPadding: {
    padding: theme.spacing(3),
    display: 'flex',
    height: `calc(100vh - ${userMenuHeight}px)`,
    overflow: 'auto',
  },
  bookButton: {
    marginRight: theme.spacing(2),
  },
  userBarBase: {
    position: 'fixed',
    zIndex: 40,
    top: 0,
    right: 0,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    height: `${userMenuHeight}px`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'white',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  userBar: {
    left: `${drawerWidth}px`,

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  userBarEmbedded: {
    left: 0,
  },
  mainWrapperBase: {
    width: 'auto',
  },
  mainWrapperPadding: {
    paddingTop: `${userMenuHeight}px`,
  },
  mainWrapper: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100vw - ${drawerWidth}px)`,
    },
  },
  mainWrapperEmbedded: {
    [theme.breakpoints.up('md')]: {
      width: 'auto',
    },
  },
}));

export type ResponsiveDrawerProps = {
  account: AccountQueryQuery['account']; //  Account;
  profile: AccountQueryQuery['profile']; // Custodian;
  usePadding: boolean;
};

const ResponsiveDrawer: React.FC<ResponsiveDrawerProps> = ({
  account,
  profile,
  usePadding,
  children,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { ehrSession, isEhrPatientContext } = useEhrContext();
  const { session } = useSessionContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const bookingDisabled = location.pathname === '/rides/new';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleBack = () => {
    if (ehrSession?.ehrRiderId) {
      // If in an EHR patient context, always go back to rider profile page
      // instead of going back in history. This is because the history stack
      // will also include all of the pages they navigated to within Portal.
      history.push(`/riders/${ehrSession.ehrRiderId}?tab=rides`, {
        from: 'ehr',
      });
    } else {
      history.goBack();
    }
  };

  const bookRideFeatureFlag = session?.featureFlags?.includes(
    'dashboard_portal_book_ride',
  );

  return (
    <div className={classes.root}>
      {isEhrPatientContext ? (
        <div className={classes.content}>
          {isEhrPatientContext && (
            <div
              className={`${classes.userBarBase} ${classes.userBarEmbedded}`}
            >
              {/* Show a back button if the user is on one of the Portal embedded pages. */}
              {location.pathname === '/profile' ? (
                <Button
                  color="primary"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              ) : (
                <div>
                  {/* This empty div is intentional to maintain layout structure with justify content's "space-between" */}
                </div>
              )}

              <FloatingAvatar />
            </div>
          )}

          <Loading />

          <div
            className={`${classes.mainWrapperBase} ${
              isEhrPatientContext && classes.mainWrapperPadding
            } ${classes.mainWrapperEmbedded}`}
          >
            <main
              className={
                usePadding
                  ? classes.contentInternalWithPadding
                  : classes.contentInternal
              }
            >
              {children}
            </main>
          </div>
        </div>
      ) : (
        <>
          <AppBar position="fixed" className={classes.appBar}>
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                className={classes.menuButton}
              >
                <MenuIcon />
              </IconButton>
              <Typography className={classes.title} variant="h6" noWrap>
                Onward
              </Typography>
              {bookRideFeatureFlag ? (
                <NewBookButton useSecondaryButton={true} />
              ) : (
                !location.pathname.includes('/rides/new') &&
                !location.pathname.includes('/copy') &&
                !location.pathname.includes('/edit') && (
                  <Button
                    className={classes.bookButton}
                    variant="contained"
                    color="secondary"
                    component={Link}
                    disabled={bookingDisabled}
                    to="/rides/new"
                  >
                    New Ride
                  </Button>
                )
              )}
              <FloatingAvatar />
            </Toolbar>
          </AppBar>
          <nav className={classes.drawer} aria-label="rides riders history">
            {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
            <Hidden mdUp implementation="css">
              <Drawer
                variant="temporary"
                anchor={theme.direction === 'rtl' ? 'right' : 'left'}
                open={mobileOpen}
                onClose={handleDrawerToggle}
                classes={{
                  paper: classes.drawerPaper,
                }}
                ModalProps={{
                  keepMounted: true, // Better open performance on mobile.
                }}
              >
                <DrawerContent account={account} profile={profile} />
              </Drawer>
            </Hidden>
            <Hidden smDown implementation="css">
              <Drawer
                classes={{
                  paper: classes.drawerPaper,
                }}
                variant="permanent"
                open
              >
                <DrawerContent account={account} profile={profile} />
              </Drawer>
            </Hidden>
          </nav>

          <div className={classes.content}>
            <div className={`${classes.userBarBase} ${classes.userBar}`}>
              <div>{/* Needed for spacing */}</div>
              <FloatingAvatar />
            </div>

            <Loading />

            <div
              className={`${classes.mainWrapperBase} ${classes.mainWrapperPadding} ${classes.mainWrapper}`}
            >
              <main
                className={
                  usePadding
                    ? classes.contentInternalWithPadding
                    : classes.contentInternal
                }
              >
                {children}
              </main>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export type ResponsiveDrawerContainerProps = {
  usePadding?: 'yes' | 'no' | 'maybe';
  getAccount?: (data: AccountQueryQuery) => void;
};
// usePadding supports 3 options:
// yes: always use, eg dashboard
// no: never use, eg ride booking
// maybe: dynamic based on screen size, eg most forms
const ResponsiveDrawerContainer: React.FC<ResponsiveDrawerContainerProps> = ({
  children,
  usePadding = 'maybe',
}) => {
  const { error, loading, session } = useSessionContext();

  const theme = useTheme();
  const xsmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const shouldUsePadding =
    (usePadding === 'yes' && !xsmallScreen) ||
    (usePadding === 'maybe' && !xsmallScreen);

  if (loading || !session) {
    return null;
  }
  if (error) {
    // ErrorHandler
    console.error(error);
    return <div>Error!</div>;
  }

  return (
    <ResponsiveDrawer
      account={session.account}
      profile={session.profile}
      usePadding={shouldUsePadding}
    >
      <RealtimeHandler account={session.account} />
      {children}
    </ResponsiveDrawer>
  );
};

export default ResponsiveDrawerContainer;
